import * as React from 'react';

import { DEFAULT_ROLE_OPTIONS_LIMIT, ROLE_OPTIONS_TYPE } from 'bundles/onboarding-common/constants';
import useLearnerObjectUpdateInterestedCareers from 'bundles/onboarding-common/hooks/useLearnerObjectUpdateInterestedCareers';
import { slugifyCustomRoleName } from 'bundles/onboarding-common/utils';
import DesiredRolesEditor from 'bundles/personalization-settings/components/DesiredRolesEditor';
import SettingRow from 'bundles/personalization-settings/components/SettingRow';
import { usePersonalizationSettingsContext } from 'bundles/personalization-settings/contexts/PersonalizationSettingsContext';
import useResolvedDesiredRoles from 'bundles/personalization-settings/hooks/useResolvedDesiredRoles';
import useRolesCatalogSearch from 'bundles/personalization-settings/hooks/useRolesCatalogSearch';
import useSettingSave from 'bundles/personalization-settings/hooks/useSettingSave';

import _t from 'i18n!nls/personalization-settings';

export type DesiredRolesSettingItemProps = {
  label: string;
};

const LABEL_ID = 'desired-roles-label';

const DesiredRolesSettingItem = ({ label }: DesiredRolesSettingItemProps) => {
  const { values, loading: readLoading, setFieldValue } = usePersonalizationSettingsContext();
  const { updateInterestedCareers } = useLearnerObjectUpdateInterestedCareers();

  const { value: seededValue, loading: resolveLoading } = useResolvedDesiredRoles(values.desiredRole);

  const [input, setInput] = React.useState('');
  const { availableRoles, loading: catalogLoading } = useRolesCatalogSearch(input);

  const {
    value: editorValue,
    notificationType,
    onChange: handleChange,
  } = useSettingSave(
    seededValue,
    async (roles) => {
      const careers = roles.map((role) =>
        role.type === 'DEFAULT'
          ? { type: 'DEFAULT' as const, id: role.id, name: role.name }
          : { type: 'CUSTOM' as const, id: slugifyCustomRoleName(role.name), name: role.name }
      );
      const result = await updateInterestedCareers(careers);
      if (result.success) {
        setFieldValue(
          'desiredRole',
          careers.length > 0
            ? careers.map((career) => ({
                id: career.id,
                name: career.name,
                type: career.type === 'DEFAULT' ? ROLE_OPTIONS_TYPE.DEFAULT : ROLE_OPTIONS_TYPE.CUSTOM,
              }))
            : null
        );
      }
      return result;
    },
    'desiredRole'
  );

  // Surfaces an attempt to add past the limit as a validation error in the same slot
  // as the save notification. A learner at the limit can only remove a chip, not add
  // one, so clearing on the next add-or-remove would never fire -- clear on blur
  // instead, since it's learner-driven and doesn't require giving anything up.
  const [overLimitAttempts, setOverLimitAttempts] = React.useState(0);

  return (
    <SettingRow
      label={label}
      labelId={LABEL_ID}
      notificationType={overLimitAttempts > 0 ? 'error' : notificationType}
      notificationMessage={
        overLimitAttempts > 0
          ? _t('You have reached the limit of #{rolesLimit} roles', { rolesLimit: DEFAULT_ROLE_OPTIONS_LIMIT })
          : undefined
      }
      notificationKey={overLimitAttempts}
      loading={readLoading || resolveLoading}
    >
      <DesiredRolesEditor
        value={editorValue}
        onChange={handleChange}
        availableRoles={availableRoles}
        onInputChange={setInput}
        loading={catalogLoading}
        onLimitExceededAttempt={() => setOverLimitAttempts((count) => count + 1)}
        onBlur={() => setOverLimitAttempts(0)}
      />
    </SettingRow>
  );
};

export default DesiredRolesSettingItem;
